import { useMemo, useState, useEffect } from "react";
import { fetchPatientAppointments, cancelAppointment, rescheduleAppointment, consultations } from "../../services/api";
import "./appointments.css";

export default function Appointments() {
  const [appointments, setAppointments] = useState([]);
  const [consultation, setConsultation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduleData, setRescheduleData] = useState({ id: null, date: "", time: "" });

  const loadData = () => {
    Promise.all([
      fetchPatientAppointments(),
      consultations.getMyLatest()
    ])
      .then(([apptsData, consultData]) => {
        setAppointments(apptsData);
        setConsultation(consultData);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setError("Failed to load data");
        setLoading(false);
      });
  };

  useEffect(() => {
    loadData();
  }, []);

  const filtered = useMemo(() => {
    return appointments.filter(a => {
      const doctorName = `${a.doctor_first_name || ''} ${a.doctor_last_name || ''}`.toLowerCase();
      const textOk = !q || doctorName.includes(q.toLowerCase()) || (a.mode && a.mode.toLowerCase().includes(q.toLowerCase()));
      const statusOk = statusFilter === "All" || (a.status || 'Upcoming').toLowerCase() === statusFilter.toLowerCase();
      return textOk && statusOk;
    });
  }, [appointments, q, statusFilter]);

  const handleCancel = async (id) => {
    if (!window.confirm("Are you sure you want to cancel this appointment?")) return;
    try {
      await cancelAppointment(id);
      alert("Appointment cancelled.");
      loadData();
    } catch (err) {
      console.error(err);
      alert("Failed to cancel.");
    }
  };
  const handleRescheduleClick = (id) => {
    setRescheduleData({ id, date: "", time: "" });
    setShowRescheduleModal(true);
  };

  const submitReschedule = async (e) => {
    e.preventDefault();
    if (!rescheduleData.date || !rescheduleData.time) return alert("Date and time are required.");
    try {
      await rescheduleAppointment(rescheduleData.id, rescheduleData.date, rescheduleData.time);
      alert("Appointment rescheduled.");
      setShowRescheduleModal(false);
      loadData();
    } catch (err) {
      console.error(err);
      alert("Failed to reschedule.");
    }
  };

  const handleConfirm = async () => {
    if (!consultation) return;
    try {
      await consultations.confirm(consultation.id);
      alert("Attendance confirmed!");
      loadData();
    } catch (err) {
      alert("Failed to confirm: " + err.message);
    }
  };

  if (loading) return <div className="appts-page">Loading appointments...</div>;
  if (error) return <div className="appts-page" style={{ color: 'red' }}>{error}</div>;

  return (
    <section className="appts-page">
      <header className="appts-head">
        <h1>Appointments</h1>
        <div className="appts-filters">
          <input placeholder="Search doctor or type…" value={q} onChange={(e) => setQ(e.target.value)} />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            {["All", "Upcoming", "Pending", "Completed"].map(s => <option key={s}>{s}</option>)}
          </select>
        </div>
      </header>

      {/* Consultation Request Status */}
      {consultation && (
        <div className="consultation-status-card" style={{ background: '#e3f2fd', padding: 20, borderRadius: 8, marginBottom: 24, border: '1px solid #90caf9' }}>
          <h3 style={{ color: '#0d47a1', marginTop: 0 }}>My Consultation Request</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
            <span className={`tag ${consultation.status.toLowerCase()}`} style={{ fontSize: 14 }}>{consultation.status}</span>
            {consultation.scheduled_date && (
              <span style={{ fontWeight: 600 }}>
                {new Date(consultation.scheduled_date).toLocaleDateString()} at {new Date(consultation.scheduled_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </div>
          {consultation.meet_link && (
            <div style={{ marginTop: 8 }}>
              <a href={consultation.meet_link} target="_blank" rel="noreferrer" style={{ color: '#1565c0', textDecoration: 'underline' }}>Join Meeting Link</a>
            </div>
          )}

          {consultation.status === 'SCHEDULED' && (
            <button
              onClick={handleConfirm}
              style={{ marginTop: 16, background: '#2e7d32', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}
            >
              Confirm Attendance
            </button>
          )}
          {consultation.status === 'CONFIRMED' && (
            <div style={{ marginTop: 12, color: '#2e7d32', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
              ✅ Attendance Confirmed
            </div>
          )}
        </div>
      )}

      <div className="appts-list">
        {filtered.map(a => (
          <div key={a.id} className="appt-card">
            <div className="info">
              <h3>{a.doctor_first_name} {a.doctor_last_name}</h3>
              <p>{new Date(a.start_time).toLocaleDateString()} • {new Date(a.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} • {a.mode}</p>
            </div>
            <span className={`tag ${(a.status || 'upcoming').toLowerCase()}`}>{a.status || 'Upcoming'}</span>
            <div className="actions">
              {(a.status || 'Upcoming').toLowerCase() !== "completed" && (
                <>
                  <button className="btn ghost" onClick={() => handleRescheduleClick(a.id)}>Reschedule</button>
                  <button className="btn danger" onClick={() => handleCancel(a.id)}>Cancel</button>
                </>
              )}
            </div>
          </div>
        ))}
        {filtered.length === 0 && <div className="empty">No appointments.</div>}
      </div>

      {showRescheduleModal && (
        <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="modal-content" style={{ background: '#fff', padding: 24, borderRadius: 8, width: 400, maxWidth: '90%' }}>
            <h3 style={{ marginTop: 0 }}>Reschedule Appointment</h3>
            <form onSubmit={submitReschedule}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 4 }}>New Date</label>
                <input type="date" required value={rescheduleData.date} onChange={e => setRescheduleData({ ...rescheduleData, date: e.target.value })} style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ccc', boxSizing: 'border-box' }} />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 4 }}>New Time</label>
                <input type="time" required value={rescheduleData.time} onChange={e => setRescheduleData({ ...rescheduleData, time: e.target.value })} style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ccc', boxSizing: 'border-box' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                <button type="button" className="btn ghost" onClick={() => setShowRescheduleModal(false)}>Cancel</button>
                <button type="submit" className="btn primary" style={{ background: '#1976d2', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 4, cursor: 'pointer' }}>Confirm</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
